import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import PageContainer from "./PageContainer";
import "../assets/portal-player.css";
import SportsBasketballIcon from "@mui/icons-material/SportsBasketball";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SchoolIcon from "@mui/icons-material/School";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ClassIcon from "@mui/icons-material/Class";
import EmailIcon from "@mui/icons-material/Email";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import HeightIcon from "@mui/icons-material/Height";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import AssessmentIcon from "@mui/icons-material/Assessment";
import StarIcon from "@mui/icons-material/Star";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import axios from "axios";
import Loader from "../components/Utils/Loader";
import {
  X_factor_getCollegeColor,
  hasLoggedEnoughMinutes,
  DEFAULT_XFACTOR_LABEL,
} from "../components/Utils/functions";
import collegeConferences, {
  getRankFromXfactor,
  classXfactorMap,
} from "../components/Utils/collegeConferences";

const PortalPlayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [gameStats, setGameStats] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [activeStatsSeasonTab, setActiveStatsSeasonTab] = useState("24-25");
  const isMobile = window.innerWidth <= 768;

  // Helper function to get the appropriate data source based on selected season
  const getSeasonData = () => {
    if (!player) return null;

    if (activeStatsSeasonTab === "23-24" && player.season_2024) {
      return {
        ...player.season_2024,
        // Fallback to main player data for fields not in season_2024
        height: player.height,
        weight: player.weight,
        eligibility: player.eligibility,
        grad_transfer: player.grad_transfer,
        personal_contact: player.personal_contact,
        other_contact: player.other_contact,
        transferring_from: player.transferring_from || player.school,
        transferring_to: player.transferring_to || player.commitment,
        portal: player.portal,
        rank: player.season_2024.rank || player.rank,
        school: player.season_2024.current_team || player.school,
        current_team: player.season_2024.current_team || player.current_team,
        commitment: player.transferring_to || player.commitment,
      };
    }

    // Default to current season (24-25) data
    return player;
  };

  // Compute the season data once per render so it can be used throughout JSX scopes
  const seasonData = getSeasonData();

  // Fetch player data
  useEffect(() => {
    const fetchPlayerData = async () => {
      try {
        setLoading(true);

        // Fetch player details from the portal API
        const response = await axios.get(`/api/portal/players/${id}`, {
          withCredentials: true,
        });

        console.log("Player API response:", response.data);

        setPlayer(response.data);

        // Set default tab to stats for non-transfer players on mobile
        if (isMobile && !response.data.portal) {
          setActiveTab("stats");
        }

        // Fetch player game logs if available
        // try {
        //   const gamesResponse = await axios.get(
        //     `/api/portal/players/${id}/games`,
        //     {
        //       withCredentials: true,
        //     }
        //   );

        //   if (gamesResponse.data && gamesResponse.data.games) {
        //     setGameStats(gamesResponse.data.games);
        //     setTotals(
        //       gamesResponse.data.totals ||
        //         calculateTotals(gamesResponse.data.games)
        //     );
        //   }
        // } catch (gamesError) {
        //   console.error("Failed to fetch game logs:", gamesError);
        //   // Not setting error here since player details could still be displayed
        // }

        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch player data:", err);
        setError("Failed to load player data. Please try again later.");
        setLoading(false);
      }
    };

    fetchPlayerData();
  }, [id]);

  // Calculate totals from game stats
  const calculateTotals = (games) => {
    if (!games || games.length === 0) return null;

    const totals = {
      opponent: "Season Totals",
      minutes_played: 0,
      fg: 0,
      fga: 0,
      fg_pct: 0,
      three_p: 0,
      three_pa: 0,
      three_p_pct: 0,
      ft: 0,
      fta: 0,
      ft_pct: 0,
      orb: 0,
      drb: 0,
      trb: 0,
      ast: 0,
      stl: 0,
      blk: 0,
      tov: 0,
      pf: 0,
      pts: 0,
    };

    games.forEach((game) => {
      totals.minutes_played += parseInt(game.minutes_played || 0);
      totals.fg += parseInt(game.fg || 0);
      totals.fga += parseInt(game.fga || 0);
      totals.three_p += parseInt(game.three_p || 0);
      totals.three_pa += parseInt(game.three_pa || 0);
      totals.ft += parseInt(game.ft || 0);
      totals.fta += parseInt(game.fta || 0);
      totals.orb += parseInt(game.orb || 0);
      totals.drb += parseInt(game.drb || 0);
      totals.trb += parseInt(game.trb || 0);
      totals.ast += parseInt(game.ast || 0);
      totals.stl += parseInt(game.stl || 0);
      totals.blk += parseInt(game.blk || 0);
      totals.tov += parseInt(game.tov || 0);
      totals.pf += parseInt(game.pf || 0);
      totals.pts += parseInt(game.pts || 0);
    });

    // Calculate percentages
    totals.fg_pct =
      totals.fga > 0
        ? ((totals.fg / totals.fga) * 100).toFixed(1) + "%"
        : "0.0%";
    totals.three_p_pct =
      totals.three_pa > 0
        ? ((totals.three_p / totals.three_pa) * 100).toFixed(1) + "%"
        : "0.0%";
    totals.ft_pct =
      totals.fta > 0
        ? ((totals.ft / totals.fta) * 100).toFixed(1) + "%"
        : "0.0%";

    // Calculate ratio
    totals.ast_to_ratio =
      totals.tov > 0
        ? (totals.ast / totals.tov).toFixed(2)
        : totals.ast.toFixed(2);

    return totals;
  };

  // Helper function to render player statistics view for a given season
  const renderPlayerStatsView = (
    statsSource,
    isPrevYearStats = false,
    mainPlayerData = null
  ) => {
    if (!statsSource) return <p>Statistics not available.</p>;

    const s = statsSource;
    const mpg = parseFloat(s.minutes_per_game) || 0;
    const games = parseFloat(s.games) || 0;

    // Always use mainPlayerData (career data) for advanced metrics if provided
    const advancedSource = mainPlayerData || s;

    // Helper to get trend arrow comparing current stat with previous season
    const getTrendArrow = (
      currentValue,
      previousValue,
      higherIsBetter = true
    ) => {
      if (isPrevYearStats || !player.season_2024) return null; // Only show arrows for current season

      // Convert to numbers and check if valid
      const current = parseFloat(currentValue);
      const previous = parseFloat(previousValue);

      if (isNaN(current) || isNaN(previous) || previous === 0) return null;

      // Calculate percent change
      const percentChange = ((current - previous) / previous) * 100;

      // Only show arrow if change is significant (more than 0.5%)
      if (Math.abs(percentChange) < 0.5) return null;

      // Determine actual direction (up = increase, down = decrease)
      const isIncreasing = current > previous;

      // Determine if change is positive based on whether higher values are better
      const isPositive = higherIsBetter ? isIncreasing : !isIncreasing;

      // Use actual direction for arrow, but color based on positive/negative impact
      return isIncreasing ? (
        <ArrowUpwardRoundedIcon
          className={`portal-player-trend-arrow ${isPositive ? "up" : "down"}`}
          fontSize="small"
        />
      ) : (
        <ArrowDownwardRoundedIcon
          className={`portal-player-trend-arrow ${isPositive ? "up" : "down"}`}
          fontSize="small"
        />
      );
    };

    // Helper to get stat value, applying TBD logic and formatting
    const getStatDisplay = (
      value,
      isPercentage = false,
      toFixed = 1,
      isRawValueForTBD = null
    ) => {
      const rawValue =
        isRawValueForTBD !== null
          ? parseFloat(isRawValueForTBD)
          : parseFloat(value);
      const displayValue = parseFloat(value);

      if (isNaN(displayValue))
        return isPrevYearStats && value === undefined ? "N/A" : "0.0"; // Handle N/A for undefined previous year stats

      if (mpg > 5 && rawValue === 0) {
        return "TBD";
      }

      if (isPercentage) {
        return isNaN(displayValue)
          ? "0.0%"
          : (displayValue * 100).toFixed(toFixed) + "%";
      }
      return isNaN(displayValue) ? "0.0" : displayValue.toFixed(toFixed);
    };

    // Per Game Stats - using values directly from the database (no division by games)
    const ppg = isPrevYearStats
      ? parseFloat(s.pts) || 0
      : parseFloat(s.ppg) || 0;
    const rpg = isPrevYearStats
      ? parseFloat(s.rebs) || 0
      : parseFloat(s.rpg) || 0;
    const apg = isPrevYearStats
      ? parseFloat(s.asts) || 0
      : parseFloat(s.apg) || 0;
    const bpg = isPrevYearStats
      ? parseFloat(s.blks) || 0
      : parseFloat(s.blks) || 0;
    const spg = isPrevYearStats
      ? parseFloat(s.stls) || 0
      : parseFloat(s.stls) || 0;
    const topg = isPrevYearStats
      ? parseFloat(s.to) || 0
      : parseFloat(s.to) || 0;

    return (
      <div className="portal-player-stats-content-wrapper">
        <div className="portal-player-season-xfactor">
          <div className="portal-player-season-xfactor-label">X-Factor</div>
          <div
            className="portal-player-season-xfactor-value"
            style={{
              color: hasLoggedEnoughMinutes(s.games, s.minutes_per_game)
                ? X_factor_getCollegeColor(s.xfactor)
                : "#666",
            }}
          >
            {!hasLoggedEnoughMinutes(s.games, s.minutes_per_game)
              ? DEFAULT_XFACTOR_LABEL
              : s.xfactor || "N/A"}
            {hasLoggedEnoughMinutes(s.games, s.minutes_per_game) &&
              getTrendArrow(s.xfactor, player.season_2024?.xfactor)}
          </div>
        </div>

        <h4>Core Stats</h4>
        <div className="portal-player-stats-grid">
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(isPrevYearStats ? s.pts : s.ppg) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {getStatDisplay(ppg, false, 1, isPrevYearStats ? s.pts : s.ppg)}
              {getTrendArrow(s.ppg, player.season_2024?.pts)}
            </div>
            <div className="portal-player-stats-label">PPG</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(isPrevYearStats ? s.rebs : s.rpg) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {getStatDisplay(rpg, false, 1, isPrevYearStats ? s.rebs : s.rpg)}
              {getTrendArrow(s.rpg, player.season_2024?.rebs)}
            </div>
            <div className="portal-player-stats-label">RPG</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(isPrevYearStats ? s.asts : s.apg) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {getStatDisplay(apg, false, 1, isPrevYearStats ? s.asts : s.apg)}
              {getTrendArrow(s.apg, player.season_2024?.asts)}
            </div>
            <div className="portal-player-stats-label">APG</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(isPrevYearStats ? s.blks : s.blks) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {getStatDisplay(bpg, false, 1, isPrevYearStats ? s.blks : s.blks)}
              {getTrendArrow(s.blks, player.season_2024?.blks)}
            </div>
            <div className="portal-player-stats-label">BPG</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(isPrevYearStats ? s.stls : s.stls) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {getStatDisplay(spg, false, 1, isPrevYearStats ? s.stls : s.stls)}
              {getTrendArrow(s.stls, player.season_2024?.stls)}
            </div>
            <div className="portal-player-stats-label">SPG</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(isPrevYearStats ? s.to : s.to) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {getStatDisplay(topg, false, 1, isPrevYearStats ? s.to : s.to)}
              {getTrendArrow(s.to, player.season_2024?.to, false)}{" "}
              {/* For TO, lower is better */}
            </div>
            <div className="portal-player-stats-label">TO</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(s.ts_pct) === 0 ? "tbd-value" : ""
              }`}
            >
              {getStatDisplay(s.ts_pct, true, 1, s.ts_pct)}
              {getTrendArrow(s.ts_pct, player.season_2024?.ts_pct)}
            </div>
            <div className="portal-player-stats-label">TS%</div>
          </div>
        </div>
        <h4>Playing Time</h4>
        <div className="portal-player-stats-grid">
          <div className="portal-player-stats-item">
            <div className="portal-player-stats-value">
              {s.games || "0"}
              {getTrendArrow(s.games, player.season_2024?.games)}
            </div>
            <div className="portal-player-stats-label">GP</div>
          </div>
          <div className="portal-player-stats-item">
            <div className="portal-player-stats-value">
              {getStatDisplay(s.minutes_per_game, false, 1, s.minutes_per_game)}
              {getTrendArrow(
                s.minutes_per_game,
                player.season_2024?.minutes_per_game
              )}
            </div>
            <div className="portal-player-stats-label">MPG</div>
          </div>
        </div>
        <h4>Advanced Metrics</h4>
        <div className="portal-player-stats-grid portal-player-advanced-section">
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.per) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.per
                ? "N/A"
                : getStatDisplay(
                    advancedSource.per,
                    false,
                    1,
                    advancedSource.per
                  )}
            </div>
            <div className="portal-player-stats-label">PER</div>
          </div>
          {/* <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.ortg) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.ortg
                ? "N/A"
                : getStatDisplay(
                    advancedSource.ortg,
                    false,
                    1,
                    advancedSource.ortg
                  )}
            </div>
            <div className="portal-player-stats-label">ORTG</div>
          </div> */}
          {/* <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.drtg) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.drtg
                ? "N/A"
                : getStatDisplay(
                    advancedSource.drtg,
                    false,
                    1,
                    advancedSource.drtg
                  )}
            </div>
            <div className="portal-player-stats-label">DRTG</div>
          </div> */}
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.pprod) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.pprod
                ? "N/A"
                : getStatDisplay(
                    advancedSource.pprod,
                    false,
                    1,
                    advancedSource.pprod
                  )}
            </div>
            <div className="portal-player-stats-label">PProd</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.orb_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.orb_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.orb_pct / 100,
                    true,
                    1,
                    advancedSource.orb_pct
                  )}
            </div>
            <div className="portal-player-stats-label">ORB%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.drb_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.drb_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.drb_pct / 100,
                    true,
                    1,
                    advancedSource.drb_pct
                  )}
            </div>
            <div className="portal-player-stats-label">DRB%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.efg_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.efg_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.efg_pct,
                    true,
                    1,
                    advancedSource.efg_pct
                  )}
            </div>
            <div className="portal-player-stats-label">eFG%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.three_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.three_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.three_pct,
                    true,
                    1,
                    advancedSource.three_pct
                  )}
            </div>
            <div className="portal-player-stats-label">3P%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.ast_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.ast_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.ast_pct / 100,
                    true,
                    1,
                    advancedSource.ast_pct
                  )}
            </div>
            <div className="portal-player-stats-label">AST%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.stl_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.stl_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.stl_pct / 100,
                    true,
                    1,
                    advancedSource.stl_pct
                  )}
            </div>
            <div className="portal-player-stats-label">STL%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.blk_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.blk_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.blk_pct / 100,
                    true,
                    1,
                    advancedSource.blk_pct
                  )}
            </div>
            <div className="portal-player-stats-label">BLK%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.tov_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.tov_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.tov_pct / 100,
                    true,
                    1,
                    advancedSource.tov_pct
                  )}
            </div>
            <div className="portal-player-stats-label">TOV%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.usg_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.usg_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.usg_pct / 100,
                    true,
                    1,
                    advancedSource.usg_pct
                  )}
            </div>
            <div className="portal-player-stats-label">USG%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.ftr) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.ftr
                ? "N/A"
                : getStatDisplay(
                    advancedSource.ftr,
                    true,
                    1,
                    advancedSource.ftr
                  )}
            </div>
            <div className="portal-player-stats-label">FTR</div>
          </div>
        </div>
      </div>
    );
  };

  const renderClassXfactorLegend = () => {
    const yearKey = activeStatsSeasonTab === "23-24" ? 2024 : 2025;
    const map = classXfactorMap[yearKey];
    if (!map) return null;

    const classesOrder = ["fr", "so", "jr", "sr"];

    return (
      <div className="ncaa-class-legend">
        {classesOrder.map((cls) =>
          map[cls] ? (
            <div key={cls} className="ncaa-class-legend-item">
              <span className="ncaa-class-legend-label">
                {cls.toUpperCase()}
              </span>
              <span
                className="ncaa-class-legend-value"
                style={{
                  color: X_factor_getCollegeColor(map[cls]),
                }}
              >
                {map[cls].toFixed(1)}
              </span>
            </div>
          ) : null
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="transfer-portal-loading">
          <Loader />
          <p>Loading player data...</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="transfer-portal-error">
          <p>{error}</p>
          {/*<button
            className="portal-player-back-link"
            type="button"
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowBackIcon />
            <span>Back</span>
          </button>*/}
        </div>
      </PageContainer>
    );
  }

  if (!player) {
    return (
      <PageContainer>
        <div className="transfer-portal-error">
          <p>Player not found.</p>
          {/*<button
            className="portal-player-back-link"
            type="button"
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowBackIcon />
            <span>Back</span>
          </button>*/}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="portal-player-container">
        <div className="portal-player-header">
          <div className="portal-player-title">
            <h1>Player Details</h1>
          </div>
          {/*<button
            className="portal-player-back-link"
            type="button"
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowBackIcon />
            <span>Back</span>
          </button>*/}
        </div>

        <div className="portal-player-content">
          {isMobile ? (
            <div className="portal-player-mobile-view">
              <div className="portal-player-profile">
                <div className="portal-player-image-container">
                  <img
                    src={
                      player.image ||
                      "https://d3mvy6uwazuxyu.cloudfront.net/back-b-2.png"
                    }
                    alt={player.name}
                    className="portal-player-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      // Use one of the backup images randomly instead of placeholder
                      const backupImages = [
                        "https://d3mvy6uwazuxyu.cloudfront.net/back-b-2.png",
                        "https://d3mvy6uwazuxyu.cloudfront.net/back-b-3.png",
                        "https://d3mvy6uwazuxyu.cloudfront.net/back-b-4.png",
                      ];
                      const randomBackup =
                        backupImages[
                          Math.floor(Math.random() * backupImages.length)
                        ];
                      e.target.src = randomBackup;
                    }}
                  />
                </div>
                <div className="portal-player-info">
                  <h2 className="portal-player-name">{player.name}</h2>
                  {(() => {
                    const seasonData = getSeasonData();
                    const notEnoughMinutes = seasonData
                      ? !hasLoggedEnoughMinutes(
                          seasonData.games,
                          seasonData.minutes_per_game
                        )
                      : false;
                    return (
                      <div className="portal-player-position-row">
                        <div className="portal-player-xfactor">
                          <span>X-Factor</span>
                          <div
                            className="portal-player-xfactor-value"
                            style={{
                              color:
                                seasonData?.rank === 0
                                  ? "#666"
                                  : X_factor_getCollegeColor(
                                      seasonData?.xfactor
                                    ),
                            }}
                          >
                            {!hasLoggedEnoughMinutes(
                              seasonData?.games,
                              seasonData?.minutes_per_game
                            )
                              ? DEFAULT_XFACTOR_LABEL
                              : seasonData?.xfactor}
                          </div>
                        </div>
                        <div className="portal-player-info-item">
                          <span className="portal-player-info-label">
                            Position
                          </span>
                          <span className="portal-player-position">
                            {seasonData?.position}
                          </span>
                        </div>
                        <div className="portal-player-info-item">
                          <span className="portal-player-info-label">
                            Class
                          </span>
                          <span className="portal-player-class">
                            {seasonData?.class}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                  {seasonData?.rank === 0 && (
                    <div className="transfer-portal-minutes-note">
                      * Didn't play much:{" "}
                      {Math.round(
                        seasonData?.games * seasonData?.minutes_per_game
                      )}{" "}
                      total mins logged
                    </div>
                  )}

                  <div className="portal-player-legend-container">
                    <div className="portal-player-legend">
                      <ul>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(145),
                            }}
                          ></span>{" "}
                          Impact (145+)
                        </li>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(130),
                            }}
                          ></span>{" "}
                          Starter (130-144)
                        </li>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(115),
                            }}
                          ></span>{" "}
                          Rotational (115-129)
                        </li>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(105),
                            }}
                          ></span>{" "}
                          Role Player (105-114)
                        </li>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(104),
                            }}
                          ></span>{" "}
                          Developmental (under 105)
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="portal-player-conference-info">
                    <div className="portal-player-detail-item">
                      <span className="portal-player-detail-label">
                        <SchoolIcon
                          fontSize="small"
                          style={{ marginRight: "4px" }}
                        />
                        School
                      </span>
                      <span className="portal-player-detail-value">
                        {seasonData?.current_team ||
                          seasonData?.school ||
                          "N/A"}
                      </span>
                    </div>

                    <div className="portal-player-detail-item">
                      <span className="portal-player-detail-label">
                        <EmojiEventsIcon
                          fontSize="small"
                          style={{ marginRight: "4px" }}
                        />
                        Conference
                      </span>
                      <span className="portal-player-detail-value">
                        {seasonData?.conf}
                      </span>
                    </div>

                    {seasonData?.team_rating && (
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          Team X-Factor
                        </span>
                        <span
                          className="portal-player-detail-value"
                          style={{
                            color: X_factor_getCollegeColor(
                              seasonData?.team_rating
                            ),
                            fontWeight: "bold",
                          }}
                        >
                          {seasonData?.team_rating}
                        </span>
                      </div>
                    )}

                    {collegeConferences[seasonData?.conf] && (
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          Conference X-Factor
                        </span>
                        <span
                          className="portal-player-detail-value"
                          style={{
                            color: X_factor_getCollegeColor(
                              collegeConferences[seasonData?.conf].xfactor
                            ),
                            fontWeight: "bold",
                          }}
                        >
                          {collegeConferences[seasonData?.conf].xfactor}
                        </span>
                      </div>
                    )}

                    <div className="portal-player-detail-item">
                      <span className="portal-player-detail-label">
                        NCAA Class X-Factor
                      </span>
                      <span
                        className="portal-player-detail-value"
                        style={{
                          color: X_factor_getCollegeColor(
                            seasonData?.class &&
                              classXfactorMap[
                                activeStatsSeasonTab === "23-24" ? 2024 : 2025
                              ] &&
                              classXfactorMap[
                                activeStatsSeasonTab === "23-24" ? 2024 : 2025
                              ][seasonData?.class.toLowerCase()]
                              ? classXfactorMap[
                                  activeStatsSeasonTab === "23-24" ? 2024 : 2025
                                ][seasonData?.class.toLowerCase()]
                              : 0
                          ),
                          fontWeight: "bold",
                        }}
                      >
                        {seasonData?.class &&
                        classXfactorMap[
                          activeStatsSeasonTab === "23-24" ? 2024 : 2025
                        ] &&
                        classXfactorMap[
                          activeStatsSeasonTab === "23-24" ? 2024 : 2025
                        ][seasonData?.class.toLowerCase()]
                          ? classXfactorMap[
                              activeStatsSeasonTab === "23-24" ? 2024 : 2025
                            ][seasonData?.class.toLowerCase()].toFixed(1)
                          : "0"}
                      </span>
                    </div>
                    {renderClassXfactorLegend()}
                  </div>

                  {/* --- Conditional Rendering for Transfer Info (Mobile) --- */}
                  {player.portal && (
                    <>
                      <div className="portal-player-transfer-status-container">
                        <div className="portal-player-schools">
                          <div className="portal-player-school from">
                            <SchoolIcon />
                            <span>
                              {player.transferring_from || player.school}
                            </span>
                          </div>
                          <ArrowForwardIcon className="portal-player-arrow" />
                          <div className="portal-player-school to">
                            {player.commitment || player.transferring_to ? (
                              <>
                                <SchoolIcon />
                                <span>
                                  {player.commitment || player.transferring_to}
                                </span>
                              </>
                            ) : (
                              <span className="portal-player-undecided">
                                Undecided
                              </span>
                            )}
                          </div>
                        </div>
                        <div
                          className={`portal-player-transfer-status ${
                            player.commitment || player.transferring_to
                              ? "committed"
                              : "available"
                          }`}
                        >
                          {player.commitment || player.transferring_to
                            ? "Committed"
                            : "Available"}
                        </div>
                      </div>

                      {/* Basic Info Tab Content (conditional details) */}
                      {activeTab === "basic" && <></>}

                      {/* Contact Tab Content (conditional) */}
                      {activeTab === "contact" && (
                        <div className="portal-player-contact">
                          <h3 className="portal-player-contact-title">
                            Contact Information
                          </h3>
                          <div className="portal-player-contact-item">
                            <EmailIcon className="portal-player-contact-icon" />
                            <div className="portal-player-contact-text">
                              {player.personal_contact ||
                                "No personal contact information available"}
                            </div>
                          </div>
                          <div className="portal-player-contact-item">
                            <ContactPhoneIcon className="portal-player-contact-icon" />
                            <div className="portal-player-contact-text">
                              {player.other_contact ||
                                "No other contact information available"}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {/* --- End Conditional Rendering --- */}
                </div>
              </div>

              <div className="portal-player-mobile-tabs">
                <div className="portal-player-tab-buttons">
                  {player.portal && (
                    <button
                      className={`portal-player-tab-button ${
                        activeTab === "basic" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("basic")}
                    >
                      Basic Info
                    </button>
                  )}
                  <button
                    className={`portal-player-tab-button ${
                      activeTab === "stats" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("stats")}
                  >
                    Season Stats
                  </button>
                  {/* Game Logs tab removed temporarily
                  <button
                    className={`portal-player-tab-button ${
                      activeTab === "games" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("games")}
                  >
                    Game Logs
                  </button>
                  */}
                  {player.portal && (
                    <button
                      className={`portal-player-tab-button ${
                        activeTab === "contact" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("contact")}
                    >
                      Contact
                    </button>
                  )}
                </div>

                {activeTab === "basic" && player.portal && (
                  <div className="portal-player-details">
                    <div className="portal-player-detail-row">
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          <HeightIcon
                            fontSize="small"
                            style={{ marginRight: "4px" }}
                          />
                          Height
                        </span>
                        <span className="portal-player-detail-value">
                          {player.height || "N/A"}
                        </span>
                      </div>
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          <FitnessCenterIcon
                            fontSize="small"
                            style={{ marginRight: "4px" }}
                          />
                          Weight
                        </span>
                        <span className="portal-player-detail-value">
                          {player.weight ? `${player.weight} lbs` : "N/A"}
                        </span>
                      </div>
                    </div>
                    <div className="portal-player-detail-row">
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          Years Remaining
                        </span>
                        <span className="portal-player-detail-value">
                          {player.eligibility || "N/A"}
                        </span>
                      </div>
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          Grad Transfer
                        </span>
                        <span className="portal-player-detail-value">
                          {player.grad_transfer ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "stats" && (
                  <div className="portal-player-stats-container">
                    <div className="portal-player-season-tabs">
                      <button
                        className={`portal-player-season-tab-button ${
                          activeStatsSeasonTab === "24-25" ? "active" : ""
                        }`}
                        onClick={() => setActiveStatsSeasonTab("24-25")}
                      >
                        2024-25 Stats
                      </button>
                      {player.season_2024 && (
                        <button
                          className={`portal-player-season-tab-button ${
                            activeStatsSeasonTab === "23-24" ? "active" : ""
                          }`}
                          onClick={() => setActiveStatsSeasonTab("23-24")}
                        >
                          2023-24 Stats
                        </button>
                      )}
                    </div>
                    {activeStatsSeasonTab === "24-25" &&
                      renderPlayerStatsView(player, false)}
                    {activeStatsSeasonTab === "23-24" &&
                      player.season_2024 &&
                      renderPlayerStatsView(player.season_2024, true, player)}
                  </div>
                )}

                {/* Game Logs tab content commented out temporarily
                {activeTab === "games" && (
                  <div className="portal-player-games-container">
                    <h3 className="portal-player-games-title">Game Logs</h3>
                    {gameStats && gameStats.length > 0 ? (
                      <div className="portal-player-table-wrapper">
                        <table className="portal-player-games-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>OPP</th>
                              <th>MIN</th>
                              <th>PTS</th>
                              <th>REB</th>
                              <th>AST</th>
                              <th>STL</th>
                              <th>BLK</th>
                              <th>TO</th>
                            </tr>
                          </thead>
                          <tbody>
                            {gameStats.map((game, index) => (
                              <tr key={index}>
                                <td>
                                  {new Date(game.game_date).toLocaleDateString(
                                    "en-US",
                                    { month: "numeric", day: "numeric" }
                                  )}
                                </td>
                                <td>{game.opponent}</td>
                                <td>{game.minutes_played || 0}</td>
                                <td>{game.pts || 0}</td>
                                <td>{game.trb || 0}</td>
                                <td>{game.ast || 0}</td>
                                <td>{game.stl || 0}</td>
                                <td>{game.blk || 0}</td>
                                <td>{game.tov || 0}</td>
                              </tr>
                            ))}
                            {totals && (
                              <tr className="portal-player-totals-row">
                                <td colSpan="2">Totals</td>
                                <td>{totals.minutes_played}</td>
                                <td>{totals.pts}</td>
                                <td>{totals.trb}</td>
                                <td>{totals.ast}</td>
                                <td>{totals.stl}</td>
                                <td>{totals.blk}</td>
                                <td>{totals.tov}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="portal-player-no-games">
                        No game logs available
                      </p>
                    )}
                  </div>
                )}
                */}

                {activeTab === "contact" && player.portal && (
                  <div className="portal-player-contact">
                    <h3 className="portal-player-contact-title">
                      Contact Information
                    </h3>
                    <div className="portal-player-contact-item">
                      <EmailIcon className="portal-player-contact-icon" />
                      <div className="portal-player-contact-text">
                        {player.personal_contact ||
                          "No personal contact information available"}
                      </div>
                    </div>
                    <div className="portal-player-contact-item">
                      <ContactPhoneIcon className="portal-player-contact-icon" />
                      <div className="portal-player-contact-text">
                        {player.other_contact ||
                          "No other contact information available"}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Desktop Layout */
            <>
              <div className="portal-player-profile-row">
                <div className="portal-player-profile">
                  <div className="portal-player-image-container">
                    <img
                      src={
                        player.image ||
                        "https://d3mvy6uwazuxyu.cloudfront.net/back-b-2.png"
                      }
                      alt={player.name}
                      className="portal-player-image"
                      onError={(e) => {
                        e.target.onerror = null;
                        const backupImages = [
                          "https://d3mvy6uwazuxyu.cloudfront.net/back-b-2.png",
                          "https://d3mvy6uwazuxyu.cloudfront.net/back-b-3.png",
                          "https://d3mvy6uwazuxyu.cloudfront.net/back-b-4.png",
                        ];
                        const randomBackup =
                          backupImages[
                            Math.floor(Math.random() * backupImages.length)
                          ];
                        e.target.src = randomBackup;
                      }}
                    />
                  </div>
                  <div className="portal-player-info">
                    <h2 className="portal-player-name">{player.name}</h2>
                    <div className="portal-player-xfactor">
                      <span>X-Factor</span>
                      <div
                        className="portal-player-xfactor-value"
                        style={{
                          color:
                            getSeasonData()?.rank === 0
                              ? "#666"
                              : X_factor_getCollegeColor(
                                  getSeasonData()?.xfactor
                                ),
                        }}
                      >
                        {!hasLoggedEnoughMinutes(
                          getSeasonData()?.games,
                          getSeasonData()?.minutes_per_game
                        )
                          ? DEFAULT_XFACTOR_LABEL
                          : getSeasonData()?.xfactor}
                      </div>
                    </div>
                    {(() => {
                      const seasonData = getSeasonData();
                      const notEnoughMinutes = seasonData
                        ? !hasLoggedEnoughMinutes(
                            seasonData.games,
                            seasonData.minutes_per_game
                          )
                        : false;
                      return (
                        <div className="portal-player-position-row">
                          <div className="portal-player-info-item">
                            <span className="portal-player-info-label">
                              Position
                            </span>
                            <span className="portal-player-position">
                              {seasonData?.position}
                            </span>
                          </div>
                          <div className="portal-player-info-item">
                            <span className="portal-player-info-label">
                              Class
                            </span>
                            <span className="portal-player-class">
                              {seasonData?.class}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                    {getSeasonData()?.rank === 0 && (
                      <div className="transfer-portal-minutes-note">
                        * Didn't play much:{" "}
                        {Math.round(
                          getSeasonData()?.games *
                            getSeasonData()?.minutes_per_game
                        )}{" "}
                        total mins logged
                      </div>
                    )}
                    <div className="portal-player-legend-container">
                      <div className="portal-player-legend">
                        <ul>
                          <li>
                            <span
                              style={{
                                backgroundColor: X_factor_getCollegeColor(145),
                              }}
                            ></span>{" "}
                            Impact (145+)
                          </li>
                          <li>
                            <span
                              style={{
                                backgroundColor: X_factor_getCollegeColor(130),
                              }}
                            ></span>{" "}
                            Starter (130-144)
                          </li>
                          <li>
                            <span
                              style={{
                                backgroundColor: X_factor_getCollegeColor(115),
                              }}
                            ></span>{" "}
                            Rotational (115-129)
                          </li>
                          <li>
                            <span
                              style={{
                                backgroundColor: X_factor_getCollegeColor(105),
                              }}
                            ></span>{" "}
                            Role Player (105-114)
                          </li>
                          <li>
                            <span
                              style={{
                                backgroundColor: X_factor_getCollegeColor(104),
                              }}
                            ></span>{" "}
                            Developmental (under 105)
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="portal-player-conference-info">
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          <SchoolIcon
                            fontSize="small"
                            style={{ marginRight: "4px" }}
                          />
                          School
                        </span>
                        <span className="portal-player-detail-value">
                          {getSeasonData()?.current_team ||
                            getSeasonData()?.school ||
                            "N/A"}
                        </span>
                      </div>

                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          <EmojiEventsIcon
                            fontSize="small"
                            style={{ marginRight: "4px" }}
                          />
                          Conference
                        </span>
                        <span className="portal-player-detail-value">
                          {getSeasonData()?.conf}
                        </span>
                      </div>

                      {getSeasonData()?.team_rating && (
                        <div className="portal-player-detail-item">
                          <span className="portal-player-detail-label">
                            Team X-Factor
                          </span>
                          <span
                            className="portal-player-detail-value"
                            style={{
                              color: X_factor_getCollegeColor(
                                getSeasonData()?.team_rating
                              ),
                              fontWeight: "bold",
                            }}
                          >
                            {getSeasonData()?.team_rating}
                          </span>
                        </div>
                      )}

                      {collegeConferences[getSeasonData()?.conf] && (
                        <div className="portal-player-detail-item">
                          <span className="portal-player-detail-label">
                            Conference X-Factor
                          </span>
                          <span
                            className="portal-player-detail-value"
                            style={{
                              color: X_factor_getCollegeColor(
                                collegeConferences[getSeasonData()?.conf]
                                  .xfactor
                              ),
                              fontWeight: "bold",
                            }}
                          >
                            {collegeConferences[getSeasonData()?.conf].xfactor}
                          </span>
                        </div>
                      )}

                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          NCAA Class X-Factor
                        </span>
                        <span
                          className="portal-player-detail-value"
                          style={{
                            color: X_factor_getCollegeColor(
                              getSeasonData()?.class &&
                                classXfactorMap[
                                  activeStatsSeasonTab === "23-24" ? 2024 : 2025
                                ] &&
                                classXfactorMap[
                                  activeStatsSeasonTab === "23-24" ? 2024 : 2025
                                ][getSeasonData()?.class.toLowerCase()]
                                ? classXfactorMap[
                                    activeStatsSeasonTab === "23-24"
                                      ? 2024
                                      : 2025
                                  ][getSeasonData()?.class.toLowerCase()]
                                : 0
                            ),
                            fontWeight: "bold",
                          }}
                        >
                          {getSeasonData()?.class &&
                          classXfactorMap[
                            activeStatsSeasonTab === "23-24" ? 2024 : 2025
                          ] &&
                          classXfactorMap[
                            activeStatsSeasonTab === "23-24" ? 2024 : 2025
                          ][getSeasonData()?.class.toLowerCase()]
                            ? classXfactorMap[
                                activeStatsSeasonTab === "23-24" ? 2024 : 2025
                              ][getSeasonData()?.class.toLowerCase()].toFixed(1)
                            : "0"}
                        </span>
                      </div>
                      {renderClassXfactorLegend()}
                    </div>

                    {/* --- Conditional Rendering for Transfer Info (Desktop) --- */}
                    {player.portal && (
                      <>
                        <div className="portal-player-transfer-status-container">
                          <div className="portal-player-schools">
                            <div className="portal-player-school from">
                              <SchoolIcon />
                              <span>
                                {player.transferring_from || player.school}
                              </span>
                            </div>
                            <ArrowForwardIcon className="portal-player-arrow" />
                            <div className="portal-player-school to">
                              {player.commitment || player.transferring_to ? (
                                <>
                                  <SchoolIcon />
                                  <span>
                                    {player.commitment ||
                                      player.transferring_to}
                                  </span>
                                </>
                              ) : (
                                <span className="portal-player-undecided">
                                  Undecided
                                </span>
                              )}
                            </div>
                          </div>
                          <div
                            className={`portal-player-transfer-status ${
                              player.commitment || player.transferring_to
                                ? "committed"
                                : "available"
                            }`}
                          >
                            {player.commitment || player.transferring_to
                              ? "Committed"
                              : "Available"}
                          </div>
                        </div>

                        <div className="portal-player-details">
                          <div className="portal-player-detail-row">
                            <div className="portal-player-detail-item">
                              <span className="portal-player-detail-label">
                                <HeightIcon
                                  fontSize="small"
                                  style={{ marginRight: "4px" }}
                                />
                                Height
                              </span>
                              <span className="portal-player-detail-value">
                                {player.height || "N/A"}
                              </span>
                            </div>
                            <div className="portal-player-detail-item">
                              <span className="portal-player-detail-label">
                                <FitnessCenterIcon
                                  fontSize="small"
                                  style={{ marginRight: "4px" }}
                                />
                                Weight
                              </span>
                              <span className="portal-player-detail-value">
                                {player.weight ? `${player.weight} lbs` : "N/A"}
                              </span>
                            </div>
                          </div>
                          <div className="portal-player-detail-row">
                            <div className="portal-player-detail-item">
                              <span className="portal-player-detail-label">
                                Years Remaining
                              </span>
                              <span className="portal-player-detail-value">
                                {player.eligibility || "N/A"}
                              </span>
                            </div>
                            <div className="portal-player-detail-item">
                              <span className="portal-player-detail-label">
                                Grad Transfer
                              </span>
                              <span className="portal-player-detail-value">
                                {player.grad_transfer ? "Yes" : "No"}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="portal-player-contact">
                          <h3 className="portal-player-contact-title">
                            Contact Information
                          </h3>
                          <div className="portal-player-contact-item">
                            <EmailIcon className="portal-player-contact-icon" />
                            <div className="portal-player-contact-text">
                              {player.personal_contact ||
                                "No personal contact information available"}
                            </div>
                          </div>
                          <div className="portal-player-contact-item">
                            <ContactPhoneIcon className="portal-player-contact-icon" />
                            <div className="portal-player-contact-text">
                              {player.other_contact ||
                                "No other contact information available"}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    {/* --- End Conditional Rendering --- */}
                  </div>
                </div>

                <div className="portal-player-stats-card">
                  <h3 className="portal-player-card-title">
                    <AssessmentIcon style={{ marginRight: "8px" }} />
                    Player Stats
                  </h3>
                  <div className="portal-player-season-tabs">
                    <button
                      className={`portal-player-season-tab-button ${
                        activeStatsSeasonTab === "24-25" ? "active" : ""
                      }`}
                      onClick={() => setActiveStatsSeasonTab("24-25")}
                    >
                      2024-25
                    </button>
                    {player.season_2024 && (
                      <button
                        className={`portal-player-season-tab-button ${
                          activeStatsSeasonTab === "23-24" ? "active" : ""
                        }`}
                        onClick={() => setActiveStatsSeasonTab("23-24")}
                      >
                        2023-24
                      </button>
                    )}
                  </div>
                  {activeStatsSeasonTab === "24-25" &&
                    renderPlayerStatsView(player, false)}
                  {activeStatsSeasonTab === "23-24" &&
                    player.season_2024 &&
                    renderPlayerStatsView(player.season_2024, true, player)}
                </div>
              </div>

              {/* Desktop view Game Logs section commented out temporarily
              <div className="portal-player-games-card full-width">
                <h3 className="portal-player-card-title">
                  <SportsBasketballIcon style={{ marginRight: "8px" }} />
                  Game Logs
                </h3>

                <p className="portal-player-no-games">
                  Game logs are temporarily unavailable
                </p>
              </div>
              */}
            </>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default PortalPlayer;
